import { Outlet, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';

import { t } from '@web/i18n';
import {
  AppBar,
  ProfileMenu,
  Scaffold,
  Sidebar,
  SidebarDivider,
  SidebarNavLink,
  SidebarSection,
  useOrgContext,
  useProfileMenu,
} from '@web/ui/core';

import { useSettingsNav } from '@web/ui/settings/hooks';
import { fireAndForget } from '@web/ui/core/utils';

export default function SettingsLayout(): React.JSX.Element {
  const { orgSlug } = useOrgContext();
  const { user, signOut } = useProfileMenu();
  const nav = useSettingsNav();
  const navigate = useNavigate();

  // Subscribe to a window-level Escape so the user can leave the settings
  // shell with the keyboard, mirroring the mockup. No React equivalent —
  // navigation lives here, not on a single focusable element.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return;
      fireAndForget(navigate({ to: '/orgs/$orgSlug/overview', params: { orgSlug } }));
    };
    globalThis.addEventListener('keydown', onKey);
    return () => {
      globalThis.removeEventListener('keydown', onKey);
    };
  }, [navigate, orgSlug]);

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar
        brandLabel={t('settings.brand.eyebrow')}
        body={
          <>
            <SidebarSection>
              <SidebarNavLink
                icon="arrow-left"
                label={t('settings.nav.backToHome')}
                to="/orgs/$orgSlug/overview"
                params={{ orgSlug }}
              />
            </SidebarSection>
            <SidebarDivider />
            {nav.groups.map((group) => (
              <SidebarSection key={group.id}>
                <div className="px-2 pb-1.5 pt-3 font-mono text-tiny font-medium uppercase tracking-eyebrow-wider text-muted-foreground">
                  {group.label}
                </div>
                {group.items.map((item) => (
                  <SidebarNavLink
                    key={item.id}
                    icon={item.icon}
                    label={item.label}
                    to={item.to}
                    params={{ orgSlug }}
                    isActive={nav.active.id === item.id}
                  />
                ))}
              </SidebarSection>
            ))}
          </>
        }
        foot={<ProfileMenu user={user} onSignOut={signOut} />}
      />

      <Scaffold
        appBar={
          <AppBar
            leading={
              <nav className="flex items-center gap-2 text-meta text-muted-foreground">
                <span>{t('home.crumb.settings')}</span>
                <span className="text-faint-foreground">/</span>
                <span className="font-medium text-foreground">{nav.active.label}</span>
              </nav>
            }
          />
        }
        body={<Outlet />}
      />
    </div>
  );
}
